import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { NavLink, useNavigate } from 'react-router-dom'
import { useDispatch } from "react-redux";
import * as yup from "yup";
import * as authAction from '../redux/actions/authAction';
import { bindActionCreators } from 'redux';
import { Input, InputOtp } from "@heroui/react";
import { Button } from "@heroui/button";
import toast, { Toaster } from 'react-hot-toast';
import OgLogo from '../assets/ogOgLogo.png';
import { CiUser, CiLock } from "react-icons/ci";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa6";
import spinner from '../assets/tube-spinner.svg';
import axios from "axios";

const emailSchema = yup.object({
    email: yup.string().email("Invalid email").required("Email is required"),
});

const otpSchema = yup.object({
    otp: yup.string().length(6, "OTP must be 6 digits").required("OTP is required"),
});

const passwordSchema = yup.object({
    newPassword: yup.string().min(6, "At least 6 characters").required("Password is required"),
    confirmPassword: yup
        .string()
        .oneOf([yup.ref("newPassword")], "New password and confirm password do not match.h")
        .required("Confirm Password is required"),
});

const ForgotPassword = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { forgotPasswordsendOtp, verifyOtp, resetPassword } = bindActionCreators(authAction, dispatch);

    const [step, setStep] = useState(1);
    const [email, setEmail] = useState("");
    const [resetToken, setResetToken] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [otpAttempts, setOtpAttempts] = useState(0);
    const [cooldown, setCooldown] = useState(0);
    const [showResend, setShowResend] = useState(false);
    const toggleVisibility = () => setShowPassword(!showPassword);

    const emailForm = useForm({ resolver: yupResolver(emailSchema) });
    const otpForm = useForm({ resolver: yupResolver(otpSchema) });
    const passwordForm = useForm({ resolver: yupResolver(passwordSchema) });

    useEffect(() => {
        const storedStep = localStorage.getItem("forgotPasswordStep");
        const storedEmail = localStorage.getItem("forgotPasswordEmail");
        const storedToken = localStorage.getItem("forgotPasswordToken");

        if (storedStep) setStep(parseInt(storedStep));
        if (storedEmail) setEmail(storedEmail);
        if (storedToken) setResetToken(storedToken);
    }, []);

    useEffect(() => {
        const storedAttempts = localStorage.getItem("otpAttempts");
        const storedCooldown = localStorage.getItem("otpCooldown");
        if (storedAttempts) setOtpAttempts(parseInt(storedAttempts));
        if (storedCooldown) {
            const remainingTime = Math.max(0, storedCooldown - Date.now());
            if (remainingTime > 0) {
                setCooldown(remainingTime);
                setStep(2);
            }
        }
    }, []);

    useEffect(() => {
        if (cooldown > 0) {
            const timer = setInterval(() => {
                setCooldown((prev) => {
                    if (prev <= 1000) {
                        localStorage.removeItem("otpCooldown");
                        return 0;
                    }
                    return prev - 1000;
                });
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [cooldown]);

    const formatCooldown = (time) => {
        const minutes = Math.floor(time / 60000);
        const seconds = ((time % 60000) / 1000).toFixed(0);
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    // Send OTP; Step 1
    const handleEmailSubmit = async (data) => {
        setLoading(true);
        try {
            const res = await forgotPasswordsendOtp({ email: data.email });
            if (res && res.data) {
                setLoading(false);
                toast.success(res.data?.message);
                setEmail(data.email);

                localStorage.setItem("forgotPasswordStep", "2");
                localStorage.setItem("forgotPasswordEmail", data.email);

                setTimeout(() => {
                    setStep(2);
                }, 2000);
            } else {
                setLoading(false);
                toast.success(res.data?.message);
            }
        } catch (error) {
            setLoading(false);
            toast.error(error.response?.data?.message || "Error sending OTP");
        }
    };

    // Verify OTP; Step 2
    const handleOTPSubmit = async (data) => {
        setLoading(true);
        setShowResend(true);
        try {
            const res = await verifyOtp({ email, otp: data.otp });
            if (res && res.data) {
                setShowResend(false);
                setLoading(false);
                toast.success(res.data.message);
                setResetToken(res.data.resetToken);

                localStorage.setItem("forgotPasswordStep", "3");
                localStorage.setItem("forgotPasswordToken", res.data.resetToken);

                setTimeout(() => {
                    setStep(3);
                }, 2000);
            } else {
                setLoading(false);
                setShowResend(true);
                toast.success(res.data.message);
                setTimeout(() => {
                    setStep(2);
                }, 2000);
            }
        } catch (error) {
            setLoading(false);
            toast.error(error.response?.data?.message || "Invalid OTP");
        }
    };

    // Reset Password; Step 3
    const handlePasswordSubmit = async (data) => {
        setLoading(true);
        try {
            const result = await resetPassword({ resetToken, newPassword: data.newPassword, confirmPassword: data.confirmPassword });
            if (result && result.data) {
                setLoading(false);
                toast.success(result.data.message);
                toast.success("Please log in.");

                setTimeout(() => {
                    navigate("/login");
                }, 2000);

                localStorage.removeItem("forgotPasswordStep");
                localStorage.removeItem("forgotPasswordEmail");
                localStorage.removeItem("forgotPasswordToken");
                localStorage.removeItem("otpAttempts");
                localStorage.removeItem("otpCooldown");                
            } else {
                setLoading(false);
                toast.success(result.data?.message);
            }
            setStep(1);
        } catch (error) {
            setLoading(false);
            toast.error(error.response?.data?.message || "Error resetting password");
        }
    };

    // Resend OTP;

    const handleResendOTP = async () => {
        if (otpAttempts >= 2) {
            const nextCooldown = Date.now() + 10 * 60 * 1000;
            setCooldown(nextCooldown - Date.now());
            localStorage.setItem("otpCooldown", nextCooldown);
            toast.error("Too many attempts. Try again in 10 minutes.");
            return;
        }

        let nextCooldown;
        if (otpAttempts === 0) {
            nextCooldown = Date.now() + 30 * 1000;
        } else if (otpAttempts === 1) {
            nextCooldown = Date.now() + 60 * 1000;
        } else {
            nextCooldown = Date.now() + 10 * 60 * 1000;
        }
        try {
            const res = await forgotPasswordsendOtp({ email });
            if (res && res.data) {
                toast.success("New OTP sent to your email.");
                setOtpAttempts(otpAttempts + 1);
                localStorage.setItem("otpAttempts", (otpAttempts + 1).toString());
                setCooldown(nextCooldown - Date.now());
                localStorage.setItem("otpCooldown", nextCooldown);
            }
        } catch (error) {
            toast.error("Error resending OTP");
        }
    };

    return (
        <div className=" flex justify-center items-center h-screen bg-gradient-to-b from-gradiant1 to-gradiant2">
            {/* <div className="flex justify-center items-center h-screen"> */}
            <div className="mx-auto w-full flex justify-center items-center custom-bg lg:p-36 md:p-10 sm:20 p-8">
                {/* <div className="w-full  max-w-md mx-auto p-8 bg-white rounded shadow"> */}
                <div className='w-full max-w-xl bg-white p-16 flex flex-col justify-center items-center shadow-global rounded-global'>
                    <img
                        src={OgLogo}
                        alt="Placeholder Image"
                        className="object-cover w-20 h-20"
                    />
                    <h1 className="text-3xl m-0  font-bold pt-2 pb-10">Forgot Password</h1>
                    {step === 1 && (
                        <form onSubmit={emailForm.handleSubmit(handleEmailSubmit)} className="w-full flex  flex-col flex-colspace-y-6 gap-5">
                            <div className="flex flex-col gap-8 w-full">
                                <Input
                                    {...emailForm.register("email")}
                                    label="Email"
                                    name="email"
                                    labelPlacement='outside'
                                    type="email"
                                    classNames={{ inputWrapper: "rounded-lg border-gray-500 focus:outline-none" }}
                                    className='rounded-lg'
                                    size="lg"
                                    variant='bordered'
                                    placeholder="Enter your email"
                                    startContent={
                                        <CiUser className='text-2xl text-gray-400' />
                                    }
                                    isInvalid={emailForm.formState.errors.email ? true : false}
                                    errorMessage={emailForm.formState.errors.email && emailForm.formState.errors.email?.message}
                                />
                            </div>
                            <Button className="w-full py-2 px-4 bg-primary hover:bg-hover text-black font-semibold rounded-md flex flex-row-reverse change-svgcolor" type="submit">
                                {loading ? <><img src={spinner} alt="" />Sending OTP..</> : "Send OTP"}
                            </Button>
                        </form>
                    )}
                    {step === 2 && (
                        <form onSubmit={otpForm.handleSubmit(handleOTPSubmit)} className="w-full flex  flex-col flex-colspace-y-6 gap-5">
                            <div className="flex flex-col items-center justify-center gap-0 w-full">
                                {/* <Input
                                    {...otpForm.register("otp")}
                                    label="OTP"
                                    type="text"
                                    placeholder="Enter 6-digit OTP"
                                /> */}
                                <div className="text-default-500 pb-1">Enter 6-digit code</div>
                                <InputOtp
                                    {...otpForm.register("otp")}
                                    label="OTP"
                                    length={6}
                                    name="otp"
                                    isDisabled={cooldown > 0}
                                    labelPlacement='outside'
                                    placeholder="Enter 6-digit code"
                                    classNames={{ inputWrapper: "rounded-lg border-gray-500 focus:outline-none" }}
                                    className='rounded-lg text-center'
                                    isInvalid={otpForm.formState.errors.otp ? true : false}
                                    errorMessage={otpForm.formState.errors.otp && otpForm.formState.errors.otp?.message}
                                />
                                {showResend && (
                                    cooldown > 0
                                        ? <p className="pt-4 text-[green]">Resend OTP in <span className="font-bold">{formatCooldown(cooldown)}</span></p>
                                        : <button onClick={handleResendOTP} className="text-orange-700 hover:text-secondary-600 pt-4">Resend OTP</button>
                                )}

                            </div>
                            <Button type="submit" className="w-full py-2 px-4 bg-primary hover:bg-hover text-black font-semibold rounded-md flex flex-row-reverse change-svgcolor" >
                                {loading ? <><img src={spinner} alt="" />Verifying OTP..</> : "Verify OTP"}
                            </Button>

                        </form>
                    )}
                    {step === 3 && (
                        <form onSubmit={passwordForm.handleSubmit(handlePasswordSubmit)} className="w-full flex  flex-col flex-colspace-y-6 gap-5">
                            <div className="flex flex-col gap-8 w-full">
                                <Input
                                    {...passwordForm.register("newPassword")}
                                    label="New Password"
                                    type="password"
                                    classNames={{ inputWrapper: "rounded-lg border-gray-500 focus:outline-none" }}
                                    labelPlacement='outside'
                                    placeholder="Enter new password"
                                    className='rounded-lg'
                                    size="lg"
                                    variant='bordered'
                                    startContent={
                                        <CiLock className='text-2xl text-gray-400' />
                                    }
                                    isInvalid={passwordForm.formState.errors.newPassword ? true : false}
                                    errorMessage={passwordForm.formState.errors.newPassword && passwordForm.formState.errors.newPassword?.message}
                                />
                            </div>
                            <div className="flex flex-col gap-8 w-full">
                                <Input
                                    {...passwordForm.register("confirmPassword")}
                                    label="Confirm Password"
                                    type={showPassword ? "text" : "password"}
                                    labelPlacement='outside'
                                    className='rounded-lg'
                                    classNames={{ inputWrapper: "rounded-lg border-gray-500 focus:outline-none" }}
                                    placeholder="Confirm new password"
                                    size="lg"
                                    variant='bordered'
                                    startContent={
                                        <CiLock className='text-2xl text-gray-400' />
                                    }
                                    endContent={
                                        <button
                                            type="button"
                                            onClick={toggleVisibility}
                                            className="focus:outline-none"
                                        >
                                            {showPassword ? <FaRegEyeSlash className="text-2xl text-gray-500" /> : <FaRegEye className="text-2xl text-gray-500" />}
                                        </button>
                                    }
                                    isInvalid={passwordForm.formState.errors.confirmPassword ? true : false}
                                    errorMessage={passwordForm.formState.errors.confirmPassword && passwordForm.formState.errors.confirmPassword?.message}
                                />
                            </div>
                            <Button type="submit" className="w-full py-2 px-4 bg-primary hover:bg-hover text-black font-semibold rounded-md flex flex-row-reverse change-svgcolor" >
                                {loading ? <><img src={spinner} alt="" />Reset Password..</> : "Reset Password"}
                            </Button>
                        </form>
                    )}
                </div>
            </div>
            <Toaster
                position="top-right"
                reverseOrder={false}
            />
        </div>
    );
};

export default ForgotPassword;
